import { supabase } from "./supabase";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321";

interface CreateOrderPayload {
  distribution_type: string;
  livestock_id: string;
  quantity: number;
  names: string[][];
  contact: { name: string; email: string; phone: string };
  referral_slug?: string | null;
  delivery_fee?: number;
  portion_size?: string;
  delivery_address?: string;
  postal_code?: string;
}

interface CreateOrderResponse {
  checkout_url: string;
  tracking_code: string;
}

export async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to create order");
  }

  return res.json();
}

export interface OrderItemResponse {
  id: string;
  names: string[];
  certificate_urls: string[];
  before_photo_url: string | null;
  after_photo_url: string | null;
}

export interface OrderResponse {
  tracking_code: string;
  distribution_type: string;
  quantity: number;
  unit_price: number;
  order_items: OrderItemResponse[];
  total_amount: number;
  delivery_fee: number;
  portion_size: string | null;
  delivery_address: string | null;
  postal_code: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  confirmed_at: string | null;
  created_at: string;
  livestock: {
    id: string;
    animal: string;
    country: string;
    country_flag: string;
    emoji: string;
    display_price: string;
    photo_type: string;
    photo_timeline: string | null;
    certificate: boolean;
    meat_description: string;
    delivery_fees: string | null;
    portions_per_animal: number;
    status: string;
    fulfilled_at: string | null;
    meat_delivered_at: string | null;
  };
}

export async function getOrder(trackingCode: string): Promise<OrderResponse> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/get-order/${trackingCode}`, {
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Order not found");
  }

  return res.json();
}

export async function submitReview(payload: {
  tracking_code: string;
  display_name: string;
  rating: number;
  quote: string;
}): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-review`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to submit review");
  }
}

export async function recoverTrackers(email: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/recover-trackers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    throw new Error("Failed to send tracker recovery email");
  }
}
